/**
 * InfoButton - Help icon that opens an explanatory modal
 *
 * Use this to provide contextual help and guidance for complex features.
 */
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useState } from 'react';

import clsx from 'clsx';
import { IoInformationCircle } from 'react-icons/io5';

import Modal from '@/components/Modal/Modal';

export interface InfoButtonProps {
  /** Modal title */
  title: string;
  /** Modal content - can be a string or JSX for rich formatting */
  children: ReactNode;
  /** Size of the icon */
  size?: 'sm' | 'md';
  /** Additional class names */
  className?: string;
}

export default function InfoButton({ title, children, size = 'sm', className }: InfoButtonProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={clsx(
          'text-ash hover:text-pearl inline-flex items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          size === 'sm' ? 'h-4 w-4' : 'h-5 w-5',
          className,
        )}
        aria-label={`Learn more about ${title}`}
      >
        <IoInformationCircle className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />
      </button>

      <Modal isOpen={isOpen} onClose={handleClose} title={title} size="md">
        <div className="text-silver space-y-4 text-sm leading-relaxed">{children}</div>
      </Modal>
    </>
  );
}

// ============================================================================
// Pre-built Info Content Components
// ============================================================================

/**
 * Styled section for info modal content
 */
export function InfoSection({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <div>
      <h3 className="text-pearl mb-2 font-medium">{title}</h3>
      <div className="text-silver/80">{children}</div>
    </div>
  );
}

/**
 * Bullet list for info content
 */
export function InfoList({ items }: { items: string[] }): ReactElement {
  return (
    <ul className="list-inside list-disc space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/**
 * Tip/callout box
 */
export function InfoTip({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3">
      <div className="flex gap-2">
        <span className="text-blue-400">💡</span>
        <div className="text-blue-200/90">{children}</div>
      </div>
    </div>
  );
}

/**
 * Warning callout
 */
export function InfoWarning({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
      <div className="flex gap-2">
        <span className="text-amber-400">⚠️</span>
        <div className="text-amber-200/90">{children}</div>
      </div>
    </div>
  );
}
